import React, { useMemo } from "react";
import { Row, Col, Card, Timeline, Divider } from "antd";
import textStyles from "../../../Shared/Styles/TextStyles.module.scss";
import styles from "./TripCards.module.scss";
import moment from "moment";
import timelineDot from "../../../../assets/icons/timelineDot.svg";
import { useWindowSize } from "../../../../hooks/useWindowSize";
import {
  ITripCardDetails,
  ITripDetails,
} from "../../../ServiceUsers/Interfaces/ITripDetails";
import { usePlanDetailsCtx } from "../../../../context/PlanDetailsContext";
import DashBoardGmaps from "../../../DashBoard/DashBoardGmaps";

type Props = {
  leg: ITripDetails | ITripCardDetails;
  legNumber: number;
  isMobileView?: boolean;
  showStatus?: boolean;
};

function TripLegCard({ legNumber, leg, isMobileView, showStatus }: Props) {
  const { width } = useWindowSize();
  const { tripDetailsInfo } = usePlanDetailsCtx();
  const span = isMobileView ? 24 : 12;

  const pickupDetails = useMemo(function() {
    const firstName = leg?.serviceUser.split(" ")[0];
    const requestTime = moment(leg?.requestPickTime).format("hh:mm A");
    const appointmentTime = moment(leg?.appointmentTime).format("hh:mm A");

    if (leg?.isWillCall) {
      return {
        time: `I'll call when ${firstName} is ready to be picked up`,
        message: `Call us at ${tripDetailsInfo.wandaCustomerCareNumber} for pick up.`,
      };
    } else if (leg?.requestPickTime) {
      return {
        time: requestTime,
        message: `Driver will arrive at the pick up address by ${requestTime}.`,
      };
    } else {
      return {
        time: appointmentTime,
        message: `${firstName} will arrive at the dropoff address by ${appointmentTime}.`,
      };
    }
  }, []);

  const statusClassMap: { [key: string]: string } = {
    3: styles.completedStatus,
    1: styles.bookedStatus,
    2: styles.canceledStatus,
  };

  return (
    <>
      <Card className={styles.cardContainer}>
        <Row gutter={[48, 16]}>
          <Col span={span}>
            <Row
              justify="space-between"
              className={styles.legTitleAlign}
              gutter={[0, 16]}
            >
              <Col>
                <Row className={styles.legTitleAlign} gutter={[8, 0]}>
                  <Col className={textStyles.normalText20}>
                    Trip {legNumber}
                  </Col>
                  <Col className={textStyles.normalText12}>
                    {leg.providerDistance} miles
                  </Col>
                </Row>
              </Col>
              {showStatus && (
                <Col className={statusClassMap[leg.tripStatusId] || ""}>
                  {leg.statusDetail}
                </Col>
              )}
              <Col span={24} />
            </Row>
            <Row className={styles.pickupTimeAlign} gutter={[8, 8]}>
              <Col className={textStyles.boldText16}>{pickupDetails.time}</Col>
              <Col className={textStyles.normalText14}>
                {!leg.isWillCall && "Pick up time"}
              </Col>
              <Col span={24} />
            </Row>
            <Row className={textStyles.normalText14}>
              {pickupDetails.message}
            </Row>

            <Divider className={styles.solidDivider} />

            <Timeline className={styles.timeline}>
              {[leg.pickUpAddress1, leg.dropOffAddress1].map(
                (address, index) => (
                  <Timeline.Item
                    key={index}
                    className={styles.timelineItem}
                    dot={<img src={timelineDot} alt="timeline" />}
                  >
                    <Row className={styles.timelineItemContent}>
                      <Col span={24} className={textStyles.boldText14}>
                        {index === 0 ? "Pick up" : "Drop off"}
                      </Col>
                      <Col span={12} className={textStyles.normalText14}>
                        <Row>
                          <Col span={24}>{address}</Col>
                          <Col span={24}>
                            {index === 0
                              ? leg.pickUpAddress2
                              : leg.dropOffAddress2}
                          </Col>
                          <Col span={24}>
                            {index === 0
                              ? `${leg.pickUpCity}, ${leg.pickUpState}`
                              : `${leg.dropOffCity}, ${leg.dropOffState}`}
                          </Col>
                        </Row>
                      </Col>
                      <Col span={12} className={textStyles.italicText14}>
                        {index === 0 ? leg.pickupNotes : leg.dropNotes}
                      </Col>
                    </Row>
                  </Timeline.Item>
                )
              )}
            </Timeline>
          </Col>
          <Col
            span={span}
            id={`map-${legNumber}`}
            className={styles.gMapContainer}
          >
            <DashBoardGmaps
              tripDetails={leg}
              width="auto"
              height={width > 992 ? "317px" : "188px"}
              hideExpandIcon={true}
            />
          </Col>
        </Row>
      </Card>
    </>
  );
}

export default TripLegCard;
